import { useState } from "react";
import { Copy, ExternalLink, LockKeyhole, SearchCheck, Sparkles, Star } from "lucide-react";
import { AdminMe, Episode, SiteVisuals } from "../lib/types";
import { getEffectiveEpisodeAccess } from "../lib/catalog-access";
import { getEpisodePrimaryAction, getSecondaryAccessAction } from "../lib/episode-actions";
import { safePatreonPostHref } from "../lib/patreon-links";
import { VaultArtwork } from "./VaultArtwork";

interface Props {
  episode: Episode;
  onLinkError?: (message: string) => void;
  visuals?: Pick<SiteVisuals, "fallbackEpisodeArtPath" | "lockedPreviewArtPath" | "cardDensity" | "episodeArtOverrides">;
  admin?: AdminMe | null;
}

function tierLabel(tier: Episode["tier_required"]) {
  if (tier === "free") return "Free preview";
  if (tier === "paid") return "Members+";
  return "Platinum";
}

function previewLabel(episode: Episode) {
  const effective = getEffectiveEpisodeAccess(episode);
  if (episode.access.unlocked) return "Unlocked";
  return effective.accessBadgeLabel;
}

function publicSeriesTitle(episode: Episode) {
  if (episode.series_slug === "uncategorized-review" || /needs mapping review/i.test(episode.series || "")) return "Vault Updates";
  return episode.series;
}

export function EpisodeCard({ episode, onLinkError, visuals, admin }: Props) {
  const [copyMessage, setCopyMessage] = useState("");
  const unlocked = episode.access.unlocked;
  const effective = getEffectiveEpisodeAccess(episode);
  const primaryAction = getEpisodePrimaryAction(episode, episode.access.accessLevel, "card");
  const secondaryAction = getSecondaryAccessAction(episode, episode.access.accessLevel);
  const badgeTier = effective.effectiveTierRequired;
  const postHref = safePatreonPostHref(episode.post_id);

  async function copyPost() {
    if (!postHref) return;
    await navigator.clipboard?.writeText(postHref).catch(() => undefined);
    setCopyMessage("Copied");
    window.setTimeout(() => setCopyMessage(""), 1400);
  }

  return (
    <article className={`episode-card density-${visuals?.cardDensity || "comfortable"}`}>
      <a href={`/episode/${episode.slug}`} className={`thumb-link ${unlocked ? "is-unlocked" : "is-locked"}`} aria-label={`Open ${episode.title}`}>
        <VaultArtwork episode={episode} src={visuals?.episodeArtOverrides?.[episode.slug]} title={episode.title} unlocked={unlocked} tier={badgeTier} variant="thumb" className="thumb" fallbackSrc={visuals?.fallbackEpisodeArtPath} lockedFallbackSrc={visuals?.lockedPreviewArtPath} seed={`${episode.series_slug}-${episode.slug}`} />
        <span className={`state ${unlocked ? "unlocked" : "locked"}`}>
          {unlocked || episode.tier_required === "free" ? <Sparkles size={14} /> : <LockKeyhole size={14} />}
          {previewLabel(episode)}
        </span>
      </a>
      <div className="card-body">
        <div className="card-row">
          <span className={`tier tier-${badgeTier}`}>{effective.accessBadgeLabel || tierLabel(episode.tier_required)}</span>
          {episode.starter_pick ? (
            <span className="starter">
              <Sparkles size={13} /> Starter
            </span>
          ) : null}
        </div>
        <h3><a href={`/episode/${episode.slug}`}>{episode.title}</a></h3>
        <p>{episode.description_short}</p>
        <div className="meta">
          <span>{publicSeriesTitle(episode)}</span>
          <span>{episode.runtime}</span>
          <span>{new Date(episode.release_date).toLocaleDateString()}</span>
        </div>
        <div className="tags">
          {episode.tags.filter((tag) => !/patreon import|platinum vault|members/i.test(tag)).slice(0, 2).map((tag) => <span key={tag}>{tag}</span>)}
        </div>
        <div className="card-actions">
          <a className="primary small" href={primaryAction.type === "navigate" ? primaryAction.href : `/episode/${episode.slug}`}>
            <Sparkles size={16} />
            {primaryAction.label}
          </a>
          {!unlocked && primaryAction.type === "navigate" && secondaryAction.href !== primaryAction.href ? (
            <a className="secondary small muted-cta" href={secondaryAction.href}>
              <LockKeyhole size={16} />
              {secondaryAction.label}
            </a>
          ) : null}
        </div>
        {admin?.authenticated ? (
          <div className="admin-card-actions" aria-label={`Admin controls for ${episode.title}`}>
            <a href={`/admin?tab=episodes&episode=${encodeURIComponent(episode.slug)}`}><SearchCheck size={15} />Organize</a>
            <a href={`/admin?tab=episodes&episode=${encodeURIComponent(episode.slug)}`}><Star size={15} />Thumbnail</a>
            <a href="/admin?tab=series"><SearchCheck size={15} />Series</a>
            {episode.review_status === "needs_review" ? <a href="/admin/fix"><SearchCheck size={15} />Fix</a> : null}
            {postHref ? <a href={postHref} target="_blank" rel="noreferrer"><ExternalLink size={15} />Post</a> : <span>Post missing</span>}
            {postHref ? <button type="button" onClick={copyPost}><Copy size={15} />{copyMessage || "Copy"}</button> : null}
          </div>
        ) : null}
      </div>
    </article>
  );
}
